import { useEffect, useMemo, useState } from 'react';
import { Chip, InputAdornment, Paper, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { post } from 'api';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import IconifyIcon from 'components/base/IconifyIcon';
import DataGridPagination from 'components/pagination/DataGridPagination';
import StyledTextField from 'components/styled/StyledTextField';

const Leaves = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
    search: '',
  });

  const columns = useMemo(
    () => [
      {
        field: 'employee',
        headerName: 'Employee',
        minWidth: 200,
        renderCell: (params) => (
          <Typography>
            {params.row.userId?.firstName} {params.row.userId?.lastName}
          </Typography>
        ),
      },
      {
        field: 'leaveType',
        headerName: 'Leave Type',
        minWidth: 150,
      },
      {
        field: 'from',
        headerName: 'From',
        minWidth: 120,
        renderCell: (params) => moment(params.row.fromDate).format('DD MMM YYYY'),
      },
      {
        field: 'to',
        headerName: 'To',
        minWidth: 120,
        renderCell: (params) => moment(params.row.toDate).format('DD MMM YYYY'),
      },
      {
        field: 'quantity',
        headerName: 'Days',
        minWidth: 100,
      },

      {
        field: 'status',
        headerName: 'Status',
        minWidth: 150,
        renderCell: (params) => (
          <Chip
            label={params.row.status}
            color={
              params.row.status === 'APPROVED'
                ? 'success'
                : params.row.status === 'REJECTED'
                  ? 'error'
                  : 'warning'
            }
          />
        ),
      },
      {
        field: 'approvedAt',
        headerName: 'Approved At',
        minWidth: 150,
        renderCell: (params) => {
          const date = params.row?.approvedAt;
          return date ? moment(date).format('DD MMM YYYY') : '-';
        },
      },
      {
        field: 'approvedBy',
        headerName: 'Approved By',
        minWidth: 150,
        renderCell: (params) => {
          const user = params.row?.approvedBy;
          return user ? (
            <Typography>
              {user?.firstName} {user?.lastName}
            </Typography>
          ) : (
            '-'
          );
        },
      },
    ],
    [],
  );

  const fetchLeaves = async () => {
    setLoading(true);
    try {
      const res = await post('/admin/getLeaves', {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        search: paginationModel.search,
        admin: true,
      });
      setRows(res.data.data);
      setTotal(res.data.total);
    } catch (err) {
      enqueueSnackbar(err?.message || 'Error fetching leaves', {
        variant: 'error',
        autoHideDuration: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPaginationModel((prev) => ({
        ...prev,
        search,
        page: 0,
      }));
    }, 600);

    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    fetchLeaves();
  }, [paginationModel]);

  return (
    <Paper sx={{ flex: 1, p: { xs: 3, md: 5 } }}>
      <StyledTextField
        type="search"
        fullWidth
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search users"
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <IconifyIcon icon="material-symbols:search-rounded" fontSize={20} />
              </InputAdornment>
            ),
          },
        }}
        sx={{
          maxWidth: { sm: 200, md: 240 },
          mb: 3,
        }}
      />
      <DataGrid
        loading={loading}
        rows={rows}
        columns={columns}
        getRowId={(row) => row._id}
        pagination
        paginationMode="server"
        rowCount={total}
        paginationModel={paginationModel} // { page: 0, pageSize: 10 }
        onPaginationModelChange={(e) => {
          setPaginationModel(() => ({
            page: e.page,
            pageSize: e.pageSize,
            search: '',
          }));
        }}
        pageSizeOptions={[5, 10, 20]}
        slots={{
          basePagination: (props) => <DataGridPagination showFullPagination {...props} />,
        }}

        // getRowId={(row) => row._id}
        // rowCount={total}
        // paginationModel={paginationModel}
        // onPaginationModelChange={(model) =>
        //   setPaginationModel({
        //     page: model.page,
        //     pageSize: model.pageSize,
        //     search,
        //   })
        // }
        // pageSizeOptions={[5, 10, 20]}
      />
    </Paper>
  );
};

export default Leaves;
